import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchGisGames, fetchGisGamesByType, searchGisGames, launchGisGame, launchJsGame } from '@/services/api/gamesService';
import { getUID } from '@/utils/helper';
import { isMobile as isMobileUtil } from '@/utils/device';
import { useScreenSize } from '@/hooks';
import { useToast } from '@/hooks/useToast';

// Constants
const DEFAULT_PROVIDERS = ['spribe', 'pragmaticplay', 'netent', 'playtech', 'bgaming'];
const GAMES_PER_PAGE = 24;
const SEARCH_DEBOUNCE_MS = 300;
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

const GIS_PROVIDERS = [
  'AmigoGaming', 'ApolloGames', 'Apparat', 'Aviatrix', 'AvatarUX',
  'BarbaraBang', 'Belatra Games', 'Betgames', 'Betsolutions', 'Betsoft', 'BFGames',
  'Big Time Gaming', 'Blueprint', 'Boldplay', 'BoomingGames',
  'Caleta', 'Charismatic', 'ChilliGames', 'ConceptGaming', 'CTInteractive',
  'Dlv', 'Dragoon Soft',
  'Elbet', 'Endorphina', 'EnjoyGaming', 'Espressogames', 'EurasianGamingSlots', 'Evoplay', 'ExcellentReel', 'Ezugi',
  'FBastards', 'Fugaso',
  'G.Games', 'Gamzix', 'GoldenRace',
  'Hacksaw Gaming', 'HoGaming', 'HolleGames Standard',
  'ICONIC21', 'Igrosoft', 'Iron Dog Studio',
  'KAGaming', 'KajotGames', 'Kalamba',
  'Lotto Instant Win',
  'MacawGaming', 'MPlay',
  'NetGame', 'No Limit City', 'Novomatic', 'Nucleus',
  'OneTouch', 'OnlyPlay',
  'PlayngoAsia', 'Playson', 'PragmaticPlay', 'PragmaticPlayLive',
  'Quickspin',
  'Red Tiger', 'RivalGames',
  'Salsa', 'Spribe', 'SuperlottoSlots',
  'Thunderkick', 'TopSpin', 'TripleCherry', 'TripleProfitsGames', 'Turbogames',
  'VibraGaming', 'Vivogaming',
  'ZeusPlay',
];

const VENDOR_DISPLAY_NAMES = {
  'amigogaming': 'AmigoGaming',
  'apollogames': 'ApolloGames',
  'apparat': 'Apparat',
  'aviatrix': 'Aviatrix',
  'avatarux': 'AvatarUX',
  'barbarabang': 'BarbaraBang',
  'belatra': 'Belatra Games',
  'betgames': 'Betgames',
  'betsolutions': 'Betsolutions',
  'betsoft': 'Betsoft',
  'bfgames': 'BFGames',
  'bigtimegaming': 'Big Time Gaming',
  'blueprint': 'Blueprint',
  'boldplay': 'Boldplay',
  'boominggames': 'BoomingGames',
  'caleta': 'Caleta',
  'charismatic': 'Charismatic',
  'chilligames': 'ChilliGames',
  'conceptgaming': 'ConceptGaming',
  'ctinteractive': 'CTInteractive',
  'dlv': 'Dlv',
  'dragoonsoft': 'Dragoon Soft',
  'elbet': 'Elbet',
  'endorphina': 'Endorphina',
  'enjoygaming': 'EnjoyGaming',
  'espressogames': 'Espressogames',
  'eurasiangamingslots': 'EurasianGamingSlots',
  'evoplay': 'Evoplay',
  'excellentreel': 'ExcellentReel',
  'ezugi': 'Ezugi',
  'fbastards': 'FBastards',
  'fugaso': 'Fugaso',
  'ggames': 'G.Games',
  'gamzix': 'Gamzix',
  'goldenrace': 'GoldenRace',
  'hacksawgaming': 'Hacksaw Gaming',
  'hogaming': 'HoGaming',
  'hollegamesstandard': 'HolleGames Standard',
  'iconic21': 'ICONIC21',
  'igrosoft': 'Igrosoft',
  'irondogstudio': 'Iron Dog Studio',
  'kagaming': 'KAGaming',
  'kajotgames': 'KajotGames',
  'kalamba': 'Kalamba',
  'lottoinstantwin': 'Lotto Instant Win',
  'macawgaming': 'MacawGaming',
  'mplay': 'MPlay',
  'netgame': 'NetGame',
  'nolimitcity': 'No Limit City',
  'novomatic': 'Novomatic',
  'nucleus': 'Nucleus',
  'onetouch': 'OneTouch',
  'onlyplay': 'OnlyPlay',
  'playngoasia': 'PlayngoAsia',
  'playson': 'Playson',
  'pragmaticplay': 'PragmaticPlay',
  'pragmaticplaylive': 'PragmaticPlayLive',
  'quickspin': 'Quickspin',
  'redtiger': 'Red Tiger',
  'rivalgames': 'RivalGames',
  'salsa': 'Salsa',
  'spribe': 'Spribe',
  'superlottoslots': 'SuperlottoSlots',
  'thunderkick': 'Thunderkick',
  'topspin': 'TopSpin',
  'triplecherry': 'TripleCherry',
  'tripleprofitsgames': 'TripleProfitsGames',
  'turbogames': 'Turbogames',
  'vibragaming': 'VibraGaming',
  'vivogaming': 'Vivogaming',
  'zeusplay': 'ZeusPlay',
};

// Provider ID mapping - maps provider display names to their provider_ids
const VENDOR_PROVIDER_IDS = {
  'AmigoGaming': 504,
  'Apparat': 743,
  'BarbaraBang': 677,
  'Belatra Games': 262,
  'Betgames': 66,
  'Blueprint': 88,
  'Boldplay': 685,
  'CTInteractive': 679,
  'Caleta': 328,
  'ConceptGaming': 540,
  'Dlv': 132,
  'Dragoon Soft': 781,
  'Elbet': 349,
  'Espressogames': 549,
  'Evoplay': 217,
  'Ezugi': 226,
  'Fugaso': 661,
  'HoGaming': 636,
  'HolleGames Standard': 756,
  'ICONIC21': 880,
  'Igrosoft': 52,
  'KAGaming': 298,
  'Lotto Instant Win': 250,
  'No Limit City': 180,
  'OneTouch': 202,
  'OnlyPlay': 624,
  'Quickspin': 78,
  'RivalGames': 766,
  'Spribe': 779,
  'SuperlottoSlots': 633,
  'Thunderkick': 84,
  'TripleCherry': 189,
  'TripleProfitsGames': 358,
  'Turbogames': 609,
  'VibraGaming': 543,
  'Vivogaming': 6,
  'ZeusPlay': 519,
};

// Helper function to get provider_id from vendor key or display name
const getProviderId = (vendorKeyOrDisplayName) => {
  // First try to get display name from vendor key
  const displayName = VENDOR_DISPLAY_NAMES[vendorKeyOrDisplayName?.toLowerCase()] || vendorKeyOrDisplayName;
  // Then get provider_id from display name
  return VENDOR_PROVIDER_IDS[displayName] || null;
};

const DEFAULT_GIS_VENDOR_KEYS = Object.keys(VENDOR_DISPLAY_NAMES).filter(k =>
  GIS_PROVIDERS.includes(VENDOR_DISPLAY_NAMES[k])
);

// Context - with default value to prevent errors
const GameContext = createContext(null);

// Utility functions
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Helper to safely parse localStorage values (removes JSON quotes if present)
const getLocalStorageValue = (key, defaultValue = '') => {
  const value = localStorage.getItem(key);
  if (!value) return defaultValue;
  
  // Try to parse if it's a JSON string (starts and ends with quotes)
  try {
    if (value.startsWith('"') && value.endsWith('"')) {
      return JSON.parse(value);
    }
    return value;
  } catch {
    return value;
  }
};

const createCacheKey = (vendor, query, page) => {
  return `${vendor}_${query}_${page}`;
};

const isValidResponse = (json) => {
  return json && json.success && Array.isArray(json.data || json.payload?.games);
};

// Image preloading helper
const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(src);
    img.onerror = reject;
    img.src = src;
  });
};

export const GameProvider = ({ children }) => {
  const { showError, showSuccess } = useToast();
  const queryClient = useQueryClient();
  const { isMobile } = useScreenSize();
  
  // Core state
  const [gameCache, setGameCache] = useState(new Map());
  const [imageCache, setImageCache] = useState(new Map());
  const [searchResults, setSearchResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [loadedProviders, setLoadedProviders] = useState(DEFAULT_PROVIDERS);
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [iframeUrl, setIframeUrl] = useState('');
  const [showIframe, setShowIframe] = useState(false);
  const [showIframeLoading, setShowIframeLoading] = useState(false);
  const [currentGame, setCurrentGame] = useState(null); // Store current game info {provider, gameId, title}
  
  // Search state
  const [currentQuery, setCurrentQuery] = useState('');
  const [selectedVendors, setSelectedVendors] = useState(DEFAULT_GIS_VENDOR_KEYS);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [gameType, setGameType] = useState('all');
  
  // Refs for optimization
  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const searchInitiatedRef = useRef(false);
  const lastFiltersRef = useRef({ sortBy: 'name', sortOrder: 'asc', gameType: 'all' });
  
  // Memoized cache operations - optimized for performance
  const getCachedData = useCallback((key) => {
    const cached = gameCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY_MS) {
      return cached.data;
    }
    return null;
  }, [gameCache]);

  const setCachedData = useCallback((key, data) => {
    setGameCache(prev => {
      // Only create new Map if key doesn't exist or data changed
      const existing = prev.get(key);
      if (existing && JSON.stringify(existing.data) === JSON.stringify(data)) {
        return prev; // Return same reference if data unchanged
      }
      const newMap = new Map(prev);
      newMap.set(key, {
        data,
        timestamp: Date.now()
      });
      return newMap;
    });
  }, []);

  // API functions
  const fetchGamesFromApi = async (vendor, query = '', page = 1, signal) => {
    const vendorName = VENDOR_DISPLAY_NAMES[vendor] || vendor;
    const cacheKey = `gis_${vendorName}_${query}_${page}_${isMobile}`;
    const reactQueryKey = ['gisGames', vendorName, page, isMobile];
    
    // First check React Query cache (from pre-fetch)
    const reactQueryData = queryClient.getQueryData(reactQueryKey);
    if (reactQueryData && !query && page === 1) {
      // Use pre-fetched data if available and no search query
      const rawGames = reactQueryData.games || [];
      const games = rawGames.map(game => ({
        id: game.uuid,
        title: game.name,
        vendor: (game.provider || vendor).toLowerCase(),
        game_icon: game.image,
        game_type: game.type,
        uuid: game.uuid,
        source: 'gis',
        provider: vendorName
      }));
      
      return {
        games,
        pagination: {
          currentPage: reactQueryData.pagination?.current_page || page,
          totalPages: reactQueryData.pagination?.total_pages || 1,
          total: reactQueryData.pagination?.total || games.length
        },
        hasMore: games.length === GAMES_PER_PAGE
      };
    }
    
    // Check internal cache
    const cached = getCachedData(cacheKey);
    if (cached) {
      return cached;
    }
    
    try {
      const result = await fetchGisGames(vendorName, page, GAMES_PER_PAGE, query, isMobile);
      
      // Store in React Query cache for future use (only first page, no search)
      if (!query && page === 1) {
        queryClient.setQueryData(reactQueryKey, {
          games: result.games || [],
          pagination: result.pagination || {
            current_page: page,
            total_pages: 1,
            total: result.games?.length || 0
          }
        });
      }
      
      const games = (result.games || []).map(game => ({
        id: game.uuid,
        title: game.name,
        vendor: (game.provider || vendor).toLowerCase(),
        game_icon: game.image,
        game_type: game.type,
        uuid: game.uuid,
        source: 'gis',
        provider: vendorName
      }));
      
      const apiResult = {
        games,
        pagination: {
          currentPage: result.pagination?.current_page || page,
          totalPages: result.pagination?.total_pages || 1,
          total: result.pagination?.total || games.length
        },
        hasMore: games.length === GAMES_PER_PAGE
      };
      
      // Cache result
      setCachedData(cacheKey, apiResult);
      
      // Preload images
      games.forEach(game => {
        if (game.game_icon && !imageCache.has(game.game_icon)) {
          preloadImage(game.game_icon)
            .then(() => setImageCache(prev => new Map(prev).set(game.game_icon, true)))
            .catch(() => null);
        }
      });
      
      return apiResult;
    } catch (error) {
      console.error(`Error fetching games for vendor ${vendor}:`, error);
      return { 
        games: [], 
        pagination: { currentPage: page, totalPages: 1, total: 0 }, 
        hasMore: false 
      };
    }
  };

  // Filter and sort games
  const filterGames = useCallback((games, filters) => {
    let filtered = [...games];
    
    if (filters.gameType && filters.gameType !== 'all') {
      filtered = filtered.filter(game => 
        game.game_type?.toLowerCase().includes(filters.gameType.toLowerCase())
      );
    }
    
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (filters.sortBy) {
        case 'vendor':
          aValue = a.vendor || '';
          bValue = b.vendor || '';
          break;
        case 'type':
          aValue = a.game_type || '';
          bValue = b.game_type || '';
          break;
        default:
          aValue = a.title || '';
          bValue = b.title || '';
      }
      
      const comparison = aValue.localeCompare(bValue);
      return filters.sortOrder === 'desc' ? -comparison : comparison;
    });
    
    return filtered;
  }, []);

  // Search games
  const searchGames = useCallback(async (query = '', vendors = [], page = 1, filters = {}, skipUpdateQuery = false) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    
    setIsLoading(true);
    setError(null);
    searchInitiatedRef.current = true;
    currentTypesRef.current = []; // Clear type-based search mode
    currentSearchQueryRef.current = ''; // Clear query-based search mode

    try {
      const searchVendors = vendors.length > 0 ? vendors : selectedVendors;
      const vendorsToUse = searchVendors.length > 0 ? searchVendors : loadedProviders;
      
      const cacheKey = createCacheKey(vendorsToUse.join(','), query, page);
      
      // First check internal cache
      const cached = getCachedData(cacheKey);
      if (cached && page === 1) {
        const filtered = filterGames(cached.games, filters);
      setSearchResults(filtered);
      setTotalCount(cached.total);
      setHasMorePages(cached.hasMore);
      setCurrentPage(page);
      
      // Update currentQuery only on first page, skip if flag is set
      if (page === 1 && !skipUpdateQuery) {
        setCurrentQuery(query);
      }
      
      setIsLoading(false);
      return filtered;
      }
      
      // If no query and first page, try to load from React Query cache (pre-fetched) FIRST
      // This prevents unnecessary API calls when cache is available
      if (!query && page === 1) {
        const cachedGamesFromReactQuery = [];
        let hasCache = false;
        
        for (const vendor of vendorsToUse) {
          const vendorName = VENDOR_DISPLAY_NAMES[vendor] || vendor;
          const reactQueryKey = ['gisGames', vendorName, 1, isMobile];
          const reactQueryData = queryClient.getQueryData(reactQueryKey);
          
          if (reactQueryData && reactQueryData.games && reactQueryData.games.length > 0) {
            hasCache = true;
            const games = reactQueryData.games.map(game => ({
              id: game.uuid,
              title: game.name,
              vendor: (game.provider || vendor).toLowerCase(),
              game_icon: game.image,
              game_type: game.type,
              uuid: game.uuid,
              source: 'gis',
              provider: vendorName
            }));
            cachedGamesFromReactQuery.push(...games);
          }
        }
        
        // If we have cached games from React Query, use them immediately (NO API CALL)
        if (hasCache && cachedGamesFromReactQuery.length > 0) {
          const uniqueGames = cachedGamesFromReactQuery.reduce((acc, game) => {
            const key = game.id || game.uuid || `${game.title}_${game.vendor}`;
            if (!acc.has(key)) {
              acc.set(key, game);
            }
            return acc;
          }, new Map());
          
          const games = Array.from(uniqueGames.values());
          const filtered = filterGames(games, filters);
          
          // Cache in internal cache for future use
          setCachedData(cacheKey, { 
            games, 
            total: games.length, 
            hasMore: games.length >= GAMES_PER_PAGE 
          });
          
          setSearchResults(filtered);
          setTotalCount(games.length);
          setHasMorePages(games.length >= GAMES_PER_PAGE);
          setCurrentPage(page);
          
          // Update currentQuery only on first page, skip if flag is set
          if (page === 1 && !skipUpdateQuery) {
            setCurrentQuery(query);
          }
          
          setIsLoading(false);
          return filtered; // Return early - NO API CALL
        }
      }
      
      // Limit concurrent requests on mobile for better performance
      const isMobileDevice = isMobile;
      const maxConcurrent = isMobileDevice ? 3 : vendorsToUse.length;
      
      const promises = vendorsToUse.map(vendor => 
        fetchGamesFromApi(vendor, query, page, signal)
          .catch(error => {
            if (error.name !== 'AbortError') {
              console.warn(`Failed to fetch games from ${vendor}:`, error);
            }
            return { games: [], pagination: { currentPage: page, totalPages: 1, total: 0 }, hasMore: false };
          })
      );
      
      // Batch requests on mobile to prevent overwhelming the device
      let results = [];
      if (isMobileDevice && promises.length > maxConcurrent) {
        for (let i = 0; i < promises.length; i += maxConcurrent) {
          const batch = promises.slice(i, i + maxConcurrent);
          const batchResults = await Promise.allSettled(batch);
          results = results.concat(batchResults);
        }
      } else {
        results = await Promise.allSettled(promises);
      }
      const successfulResults = results
        .filter(result => result.status === 'fulfilled' && result.value.games.length > 0)
        .map(result => result.value);
      
      if (successfulResults.length === 0) {
        setSearchResults([]);
        setTotalCount(0);
        setHasMorePages(false);
        setError('Unable to load games. Please check your connection and try again.');
        setIsLoading(false);
        return [];
      }
      
      const allGames = successfulResults.flatMap(result => result.games);
      const uniqueGames = allGames.reduce((acc, game) => {
        const key = game.id || game.uuid || `${game.title}_${game.vendor}`;
        if (!acc.has(key)) {
          acc.set(key, game);
        }
        return acc;
      }, new Map());
      
      const games = Array.from(uniqueGames.values());
      const hasMore = successfulResults.some(result => result.hasMore);
      const total = Math.max(...successfulResults.map(r => r.pagination?.total || r.games.length));
      
      setCachedData(cacheKey, { games, total, hasMore });
      
      const filtered = filterGames(games, filters);
      
      if (page === 1) {
        setSearchResults(filtered);
      } else {
        setSearchResults(prev => [...prev, ...filtered]);
      }
      
      setTotalCount(total);
      setHasMorePages(hasMore);
      setCurrentPage(page);
      
      // Update currentQuery only on first page to prevent re-renders
      // Skip update if skipUpdateQuery flag is set (e.g., when called from ProviderModal)
      if (page === 1 && !skipUpdateQuery) {
        setCurrentQuery(query);
      }
      
      setIsLoading(false);
      return filtered;
      
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Search error:', err);
        setError(err.message || 'Failed to search games');
        if (page === 1) {
          setSearchResults([]);
          setTotalCount(0);
          setHasMorePages(false);
        }
      }
      setIsLoading(false);
      throw err;
    }
  }, [selectedVendors, loadedProviders, getCachedData, setCachedData, filterGames, isMobile, queryClient]);

  // Wrapper for searchGames that uses current filters
  const searchGamesWithFilters = useCallback((query, vendors, page = 1, filters = {}, skipUpdateQuery = false) => {
    const filtersToUse = Object.keys(filters).length > 0 ? filters : { sortBy, sortOrder, gameType };
    return searchGames(query, vendors, page, filtersToUse, skipUpdateQuery);
  }, [searchGames, sortBy, sortOrder, gameType]);

  // Track current type-based search for pagination
  const currentTypesRef = useRef([]);

  // Search games by type (category) — calls /gis/gamesgis?type=TYPE
  // Uses the first (primary) type for paginated results to keep exactly GAMES_PER_PAGE per load
  const searchGamesByType = useCallback(async (types = [], page = 1) => {
    if (!types.length) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);
    searchInitiatedRef.current = true;
    currentTypesRef.current = types;
    currentSearchQueryRef.current = ''; // Clear query-based search mode

    try {
      const primaryType = types[0];
      const cacheKey = `type_${primaryType}_p${page}_${isMobile}`;
      const cached = getCachedData(cacheKey);
      if (cached) {
        if (page === 1) {
          setSearchResults(cached.games);
        } else {
          setSearchResults(prev => [...prev, ...cached.games]);
        }
        setTotalCount(cached.total);
        setHasMorePages(cached.hasMore);
        setCurrentPage(page);
        setIsLoading(false);
        return cached.games;
      }

      const result = await fetchGisGamesByType(primaryType, isMobile, page, GAMES_PER_PAGE);

      const games = (result.games || []).map(game => ({
        id: game.uuid,
        title: game.name,
        vendor: (game.provider || '').toLowerCase(),
        game_icon: game.image,
        game_type: game.type,
        uuid: game.uuid,
        source: 'gis',
        provider: game.provider || '',
      }));

      const hasMore = result.hasMore || false;
      const totalItems = result.pagination?.totalItems || games.length;

      setCachedData(cacheKey, { games, total: totalItems, hasMore });

      if (page === 1) {
        setSearchResults(games);
      } else {
        setSearchResults(prev => [...prev, ...games]);
      }
      setTotalCount(totalItems);
      setHasMorePages(hasMore);
      setCurrentPage(page);
      setCurrentQuery('');
      setIsLoading(false);
      return games;
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Failed to load games');
        if (page === 1) {
          setSearchResults([]);
          setTotalCount(0);
        }
        setHasMorePages(false);
      }
      setIsLoading(false);
    }
  }, [isMobile, getCachedData, setCachedData]);

  // Track current search query for pagination
  const currentSearchQueryRef = useRef('');

  // Search games by query — calls /gis/gamesgis?search=QUERY (single optimized endpoint)
  const searchGamesByQuery = useCallback(async (query, page = 1) => {
    if (!query) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);
    searchInitiatedRef.current = true;
    currentSearchQueryRef.current = query;
    currentTypesRef.current = []; // Clear type-based mode

    try {
      const cacheKey = `search_${query}_p${page}_${isMobile}`;
      const cached = getCachedData(cacheKey);
      if (cached) {
        if (page === 1) {
          setSearchResults(cached.games);
        } else {
          setSearchResults(prev => [...prev, ...cached.games]);
        }
        setTotalCount(cached.total);
        setHasMorePages(cached.hasMore);
        setCurrentPage(page);
        setIsLoading(false);
        return cached.games;
      }

      const result = await searchGisGames(query, page, GAMES_PER_PAGE, isMobile);

      const games = (result.games || []).map(game => ({
        id: game.uuid,
        title: game.name,
        vendor: (game.provider || '').toLowerCase(),
        game_icon: game.image,
        game_type: game.type,
        uuid: game.uuid,
        source: 'gis',
        provider: game.provider || '',
      }));

      const hasMore = result.hasMore || false;
      const totalItems = result.pagination?.totalItems || games.length;

      setCachedData(cacheKey, { games, total: totalItems, hasMore });

      if (page === 1) {
        setSearchResults(games);
      } else {
        setSearchResults(prev => [...prev, ...games]);
      }
      setTotalCount(totalItems);
      setHasMorePages(hasMore);
      setCurrentPage(page);
      setCurrentQuery(query);
      setIsLoading(false);
      return games;
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Failed to search games');
        if (page === 1) {
          setSearchResults([]);
          setTotalCount(0);
        }
        setHasMorePages(false);
      }
      setIsLoading(false);
    }
  }, [isMobile, getCachedData, setCachedData]);

  // Debounced search - use ref to always call latest searchGames without recreating debounced function
  const searchGamesRef = useRef(searchGames);
  useEffect(() => {
    searchGamesRef.current = searchGames;
  }, [searchGames]);

  const debouncedSearchRef = useRef(null);
  if (!debouncedSearchRef.current) {
    debouncedSearchRef.current = debounce((query, vendors, filters) => {
      searchGamesRef.current(query, vendors, 1, filters);
    }, SEARCH_DEBOUNCE_MS);
  }
  const debouncedSearch = debouncedSearchRef.current;

  // Load more games
  const loadMoreGames = useCallback(async () => {
    if (!hasMorePages || isLoading) return;

    try {
      // If we were doing a query-based search, continue with query pagination
      if (currentSearchQueryRef.current) {
        await searchGamesByQuery(currentSearchQueryRef.current, currentPage + 1);
      } else if (currentTypesRef.current.length > 0) {
        // If we were doing a type-based search, continue with type-based pagination
        await searchGamesByType(currentTypesRef.current, currentPage + 1);
      } else {
        await searchGames(currentQuery, loadedProviders, currentPage + 1, {
          sortBy,
          sortOrder,
          gameType
        });
      }
    } catch (error) {
      console.error('Load more error:', error);
      setError('Failed to load more games');
    }
  }, [hasMorePages, isLoading, currentQuery, loadedProviders, currentPage, searchGames, searchGamesByType, searchGamesByQuery, sortBy, sortOrder, gameType]);

  // Game launch - Only GIS games
  const handleGameLaunch = useCallback(async (game, navigate = null) => {
    if (!getUID()) {
      showError('Please login to play');
      return;
    }

    setIsLoading(true);
    try {
      const providerName = (game.provider || game.vendor || 'spribe').toLowerCase();
      const gameId = game.uuid || game.id;
      
      const payload = {
        player_id: getUID(),
        player_name: getLocalStorageValue('name', 'Player'),
        game_uuid: game.uuid || game.id,
        currency: getLocalStorageValue('coin', 'INR'),
        device: isMobile ? 'mobile' : 'desktop',
        return_url: 'https://api.cryptmet.com/api/gis/callback/transactions',
        language: 'en'
      };
      
      const result = await launchGisGame(payload);
      if (result.url) {
        setIframeUrl(result.url);
        setShowIframe(true);
        setShowIframeLoading(true);
        
        // Store game info for routing
        setCurrentGame({
          provider: providerName,
          gameId: gameId,
          title: game.title || game.name || 'Game',
          url: result.url
        });
        
        // Navigate to game route if navigate function provided
        if (navigate) {
          navigate(`/${providerName}/${gameId}`);
        }
      } else {
        throw new Error(result.message || 'Failed to launch game');
      }
    } catch (error) {
      console.error('Launch error:', error);
      setError(error.message || 'Failed to launch game');
      setShowIframe(false);
      setIframeUrl('');
      setShowIframeLoading(false);
      setCurrentGame(null);
      showError(error.message || 'Failed to launch game');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  // JS Game launch - For e-sports and JS games (like TFGaming)
  const handleJsGameLaunch = useCallback(async (gameUid, navigate = null) => {
    if (!getUID()) {
      showError('Please login to play');
      return;
    }

    setIsLoading(true);
    try {
      const credits = getLocalStorageValue('credit', '0');
      const coins = getLocalStorageValue('coin', 'INR');
      
      // Check currency for TFGaming (e-sports)
      if (coins !== 'USDT' && coins !== 'INR') {
        showError('Please swap to USDT or INR to play E-sports');
        setIsLoading(false);
        return;
      }
      
      const payload = {
        game_uid: gameUid,
        user_id: getUID(),
        credit_amount: credits,
        currency_code: coins,
        language: 'en'
      };
      
      const result = await launchJsGame(payload);
      if (result.game_launch_url) {
        setIframeUrl(result.game_launch_url);
        setShowIframe(true);
        setShowIframeLoading(true);
        
        // Store game info for routing
        setCurrentGame({
          provider: 'esports',
          gameId: gameUid,
          title: 'E-sports',
          url: result.game_launch_url
        });
        
        // Navigate to game route if navigate function provided
        if (navigate) {
          navigate(`/esports/${gameUid}`);
        }
      } else {
        throw new Error(result.message || 'Failed to launch game');
      }
    } catch (error) {
      console.error('JS Game launch error:', error);
      setError(error.message || 'Failed to launch game');
      setShowIframe(false);
      setIframeUrl('');
      setShowIframeLoading(false);
      setCurrentGame(null);
      showError(error.message || 'Failed to launch game');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);
  
  // Close game and clear state
  const closeGame = useCallback((navigate = null) => {
    // Navigate first, then clear state — prevents race condition
    // where state clears before navigation executes
    if (navigate) {
      const currentPath = window.location.pathname;
      const isGameRoute = currentPath.match(/^\/[^/]+\/[^/]+$/) &&
                         !currentPath.startsWith('/sports/') &&
                         !currentPath.startsWith('/match/') &&
                         !currentPath.startsWith('/menu') &&
                         !currentPath.startsWith('/trade');

      if (isGameRoute) {
        navigate('/', { replace: true });
      } else {
        navigate(-1);
      }
    }

    // Clear game state after navigation
    setTimeout(() => {
      setShowIframe(false);
      setIframeUrl('');
      setShowIframeLoading(false);
      setCurrentGame(null);
    }, 50);
  }, []);

  // Update search when filters change (only if search has been initiated)
  // This prevents automatic API calls on mount
  useEffect(() => {
    // Only trigger search if search was already initiated
    if (!searchInitiatedRef.current) {
      return;
    }

    // Check if filters actually changed
    const currentFilters = { sortBy, sortOrder, gameType };
    const filtersChanged = 
      lastFiltersRef.current.sortBy !== currentFilters.sortBy ||
      lastFiltersRef.current.sortOrder !== currentFilters.sortOrder ||
      lastFiltersRef.current.gameType !== currentFilters.gameType;

    // Only trigger search if filters changed and we have an active query or vendors
    if (filtersChanged && (currentQuery || (selectedVendors.length > 0 && selectedVendors.length !== DEFAULT_GIS_VENDOR_KEYS.length))) {
      lastFiltersRef.current = currentFilters;
      debouncedSearch(currentQuery, selectedVendors, currentFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder, gameType]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Context value
  const contextValue = useMemo(() => ({
    // Data
    searchResults,
    totalCount,
    hasMorePages,
    loadedProviders,
    
    // State
    isLoading,
    error,
    currentQuery,
    selectedVendors,
    currentPage,
    sortBy,
    sortOrder,
    gameType,
    showIframe,
    iframeUrl,
    showIframeLoading,
    currentGame,
    
    // Actions
    searchGames: searchGamesWithFilters,
    searchGamesByType,
    searchGamesByQuery,
    loadMoreGames,
    handleGameLaunch,
    handleJsGameLaunch,
    closeGame,
    setSortBy,
    setSortOrder,
    setGameType,
    setSelectedVendors,
    clearError: () => setError(null),
    
    // Constants
    vendorDisplayNames: VENDOR_DISPLAY_NAMES,
    vendorProviderIds: VENDOR_PROVIDER_IDS,
    getProviderId,
    gamesPerPage: GAMES_PER_PAGE,
    totalProviders: Object.keys(VENDOR_DISPLAY_NAMES).length
  }), [
    searchResults,
    totalCount,
    hasMorePages,
    isLoading,
    error,
    currentQuery,
    selectedVendors,
    currentPage,
    sortBy,
    sortOrder,
    gameType,
    showIframe,
    iframeUrl,
    showIframeLoading,
    currentGame,
    loadMoreGames,
    handleGameLaunch,
    handleJsGameLaunch,
    closeGame,
    searchGamesWithFilters,
    searchGamesByType,
    searchGamesByQuery,
  ]);

  return (
    <GameContext.Provider value={contextValue}>
      {children}
    </GameContext.Provider>
  );
};

export const useGames = () => {
  const context = useContext(GameContext);
  if (!context) {
    // Return a fallback context instead of throwing to prevent crashes during HMR
    console.warn('useGames called outside GameProvider, returning fallback context');
    return {
      searchResults: [],
      totalCount: 0,
      hasMorePages: false,
      loadedProviders: DEFAULT_PROVIDERS,
      isLoading: false,
      error: null,
      currentQuery: '',
      selectedVendors: DEFAULT_GIS_VENDOR_KEYS,
      currentPage: 1,
      sortBy: 'name',
      sortOrder: 'asc',
      gameType: 'all',
      showIframe: false,
      iframeUrl: '',
      showIframeLoading: false,
      currentGame: null,
      searchGames: () => Promise.resolve([]),
      searchGamesByQuery: () => Promise.resolve([]),
      loadMoreGames: () => Promise.resolve(),
      handleGameLaunch: () => Promise.resolve(),
      handleJsGameLaunch: () => Promise.resolve(),
      closeGame: () => {},
      setSortBy: () => {},
      setSortOrder: () => {},
      setGameType: () => {},
      setSelectedVendors: () => {},
      clearError: () => {},
      vendorDisplayNames: VENDOR_DISPLAY_NAMES,
      vendorProviderIds: VENDOR_PROVIDER_IDS,
      getProviderId,
      gamesPerPage: GAMES_PER_PAGE,
      totalProviders: Object.keys(VENDOR_DISPLAY_NAMES).length
    };
  }
  return context;
};
